import * as React from 'react'
import Link from 'next/link'
import { FormattedMessage, useIntl, defineMessages, FormattedDateTimeRange } from 'react-intl'
import distance from '@turf/distance'
import * as turf from '@turf/helpers'
import { isSameDay, areRangesOverlapping, addDays } from 'date-fns'
import { Layout, Header, Title, Pagination } from './utils'
import { DateDayOnly } from 'components/time'
import { Phone, Mailto } from 'components/anchors'
import { List, Entry } from 'components/description'
import { Responsive, ExpandableRow } from 'components/table'
import { Helper } from 'components/text'
import { MultiLine } from 'components/misc'
import Shim from 'components/Shim'
import { Distance, Tags } from './cells'
import { MINIMUM_DISTANCE } from '../constants'
import { NONE, DESC, ASC } from '@avcan/constants/sortings'
import { useSorting, usePagination, useFilters } from 'hooks/collection'
import { useIntlMemo } from 'hooks/intl'
import { useContext } from '../context'
import * as GeoUtils from '@avcan/utils/geo'
import { DateParam } from '@avcan/utils/params'
import { DATE } from '@avcan/constants/intl/formats'
import styles from './Courses.module.css'
import { LevelMessages } from '../constants'
import { useMessageMap } from 'hooks/intl'

export default function Courses({ data }) {
    const { params } = useContext()
    const { level, from, to, tags, sorting, place, longitude, latitude } = params.value
    const [name = null, order = NONE] = sorting
    const [page, setPage] = React.useState(1)
    const all = React.useMemo(() => {
        if (!GeoUtils.isValidLongitude(longitude) || !GeoUtils.isValidLatitude(latitude)) {
            return data
        }

        const location = turf.point([longitude, latitude])

        return data.map(course => ({
            ...course,
            distance:
                longitude && latitude
                    ? Math.max(
                          Math.round(distance(turf.point(course.loc), location)),
                          MINIMUM_DISTANCE
                      )
                    : null,
        }))
    }, [data, longitude, latitude])
    const filtered = useFilters(all, getPredicates({ level, from, to, tags }))
    const sorted = useSorting(filtered, SORTERS.get(order).get(name))
    const paginated = usePagination(sorted, page)

    function handleSortingChange(name, order) {
        params.merge({
            sorting: [name, order],
        })
    }

    React.useEffect(() => {
        setPage(1)
    }, [level, from, to, tags, sorting])

    const count = filtered.length
    const columns = useColumns()

    return (
        <Layout title={<Title type="course" count={count} total={data.length} />}>
            <Responsive>
                <table>
                    <Header
                        columns={columns}
                        sorting={sorting}
                        onSortingChange={handleSortingChange}
                        place={place}
                    />
                    <tbody>
                        {paginated.map(row => (
                            <ExpandableRow key={row.id}>
                                <tr>
                                    {columns.map(({ property, name }) => (
                                        <td key={name}>{property(row)}</td>
                                    ))}
                                </tr>
                                <tr>
                                    <td colSpan={columns.length + 1}>
                                        <Controlled
                                            description={row.description}
                                            provider={row.provider}
                                        />
                                    </td>
                                </tr>
                            </ExpandableRow>
                        ))}
                    </tbody>
                    {count === 0 && (
                        <caption>
                            <p>
                                <FormattedMessage
                                    description="Layout ast/tables/Courses"
                                    defaultMessage="No courses match your criteria, consider finding a provider on the <link>providers page</link> to contact directly."
                                    values={{
                                        link(text) {
                                            return (
                                                <Link href="./providers">
                                                    <a>{text}</a>
                                                </Link>
                                            )
                                        },
                                    }}
                                />
                            </p>
                            <p>
                                <FormattedMessage
                                    description="Layout ast/tables/Courses"
                                    defaultMessage="You can also <link>reset your criteria</link> to see them all."
                                    values={{
                                        link(text) {
                                            return (
                                                <Link href="/training/courses" scroll={false}>
                                                    <a>{text}</a>
                                                </Link>
                                            )
                                        },
                                    }}
                                />
                            </p>
                        </caption>
                    )}
                </table>
            </Responsive>
            <Pagination count={count} page={page} onChange={setPage} />
        </Layout>
    )
}

// Utils
function Controlled({ description, provider }) {
    const { name, website, email, phone, loc_description } = provider
    const intl = useIntl()

    return (
        <div className={styles.Controlled}>
            <Shim right>
                <List inline>
                    <Entry
                        term={intl.formatMessage({
                            defaultMessage: 'Description',
                            description: 'Layout ast/tables/Courses',
                        })}>
                        <MultiLine>{description}</MultiLine>
                    </Entry>
                </List>
            </Shim>
            <List>
                <Entry
                    term={intl.formatMessage({
                        defaultMessage: 'Name',
                        description: 'Layout ast/tables/Courses',
                    })}>
                    {name}
                </Entry>
                <Entry
                    term={intl.formatMessage({
                        defaultMessage: 'Website',
                        description: 'Layout ast/tables/Courses',
                    })}>
                    <a href={website} target={name}>
                        {website}
                    </a>
                </Entry>
                <Entry
                    term={intl.formatMessage({
                        defaultMessage: 'Email',
                        description: 'Layout ast/tables/Courses',
                    })}>
                    <Mailto email={email} />
                </Entry>
                <Entry
                    term={intl.formatMessage({
                        defaultMessage: 'Phone',
                        description: 'Layout ast/tables/Courses',
                    })}>
                    <Phone phone={phone} />
                </Entry>
                <Entry
                    term={intl.formatMessage({
                        defaultMessage: 'Location',
                        description: 'Layout ast/tables/Courses',
                    })}>
                    {loc_description}
                </Entry>
            </List>
        </div>
    )
}

// Utils
const messages = defineMessages({
    distanceTitle: {
        id: 'app.distanceTitle',
        defaultMessage: 'Straight line between {place} and the course.',
        description: 'Layout ast/tables/Courses',
    },
    dates: {
        defaultMessage: 'Dates',
        description: 'Layout ast/tables/Courses',
    },
    level: {
        defaultMessage: 'Level',
        description: 'Layout ast/tables/Courses',
    },
    provider: {
        defaultMessage: 'Provider',
        description: 'Layout ast/tables/Courses',
    },
    distance: {
        defaultMessage: 'Distance',
        description: 'Layout ast/tables/Courses',
    },
    location: {
        defaultMessage: 'Location',
        description: 'Layout ast/tables/Courses',
    },
    tags: {
        defaultMessage: 'Tags',
        description: 'Layout ast/tables/Courses',
    },
    cost: {
        defaultMessage: 'Cost',
        description: 'Layout ast/tables/Courses',
    },
})
function useColumns() {
    const levels = useMessageMap(LevelMessages)

    return useIntlMemo(intl => [
        {
            name: 'dates',
            title: intl.formatMessage(messages.dates),
            property({ date_start, date_end }) {
                const from = DateParam.parse(date_start)
                const to = DateParam.parse(date_end)

                if (isSameDay(date_start, date_end)) {
                    return <DateDayOnly value={date_start} />
                }

                return <FormattedDateTimeRange from={from} to={to} {...DATE} />
            },
            sorting: NONE,
        },
        {
            name: 'level',
            title: intl.formatMessage(messages.level),
            property({ level }) {
                return levels.get(level)
            },
        },
        {
            name: 'provider',
            title: intl.formatMessage(messages.provider),
            sorting: NONE,
            property({ provider }) {
                return provider.name
            },
        },
        {
            name: 'distance',
            title({ place }) {
                const text = intl.formatMessage(messages.distance)

                if (place) {
                    return (
                        <Helper title={intl.formatMessage(messages.distanceTitle, { place })}>
                            {text}
                        </Helper>
                    )
                }

                return text
            },
            property({ distance }) {
                return <Distance value={distance} />
            },
            sorting: NONE,
        },
        {
            name: 'location',
            title: intl.formatMessage(messages.location),
            property({ loc_description }) {
                return loc_description
            },
        },
        {
            name: 'tags',
            title: intl.formatMessage(messages.tags),
            property({ tags }) {
                return <Tags value={tags} />
            },
        },
        {
            name: 'cost',
            title: intl.formatMessage(messages.cost),
            property({ cost }) {
                return (
                    <span className={styles.CostCell}>
                        {cost.cost} {cost.currency}
                    </span>
                )
            },
        },
    ])
}
const SORTERS = new Map([
    [
        ASC,
        new Map([
            ['provider', (a, b) => sortByName(a, b) || sortByDate(a, b)],
            ['distance', (a, b) => sortByDistance(a, b) || sortByDate(a, b) || sortByName(a, b)],
            ['dates', (a, b) => sortByDate(a, b) || sortByName(a, b)],
        ]),
    ],
    [
        DESC,
        new Map([
            ['provider', (a, b) => sortByName(b, a) || sortByDate(a, b)],
            ['distance', (a, b) => sortByDistance(b, a) || sortByDate(a, b) || sortByName(a, b)],
            ['dates', (a, b) => sortByDate(b, a) || sortByName(a, b)],
        ]),
    ],
    [NONE, new Map()],
])
function sortByDate(a, b) {
    return new Date(a.date_start) - new Date(b.date_start)
}
function sortByDistance(a, b) {
    return a.distance - b.distance
}
function sortByName(a, b) {
    return a.provider.name.localeCompare(b.provider.name, 'en', {
        sensitivity: 'base',
    })
}
const PREDICATES = new Map([
    [
        'level',
        ({ level }) =>
            course =>
                course.level === level,
    ],
    [
        'tags',
        ({ tags = new Set() }) =>
            course =>
                tags.size === 0 ? true : course.tags.some(tag => tags.has(tag)),
    ],
    [
        'to',
        ({ from, to }) =>
            ({ date_start, date_end }) =>
                areRangesOverlapping(from, addDays(to, 1), date_start, date_end),
    ],
])

function getPredicates(props) {
    return Object.entries(props)
        .filter(([key, value]) => value && PREDICATES.has(key))
        .map(([key]) => {
            const createPredicate = PREDICATES.get(key)

            return createPredicate(props)
        })
}
